import { Show, SignIn } from '@clerk/react'
import { Badge, Banner, Loader, Surface, Text } from '@cloudflare/kumo'
import { useEffect, useState } from 'react'
import { adminApi } from './api'
import { env } from './env'
import { useGetToken } from './lib/auth'
import { toMessage } from './lib/format'
import { Shell } from './shell'
import { useLocation } from 'react-router'
import { FactoryPortalPage } from './pages/factory'
import { AdminAccessContext, OWNER_FALLBACK } from './lib/admin-access'

/** 로그인 → 어드민 권한 확인 → 사이드바 셸 */
export function App() {
  const path = useLocation().pathname
  if (path === '/factory' || path.startsWith('/factory/')) return <FactoryPortalPage />
  return (
    <>
      <Show when="signed-out">
        <div className="bg-kumo-base text-kumo-default min-h-screen">
          <header className="border-kumo-line flex items-center gap-3 border-b px-6 py-3">
            <Text variant="heading">RecipeCat 어드민</Text>
            <Badge variant={env.ENV === 'production' ? 'error' : 'primary'}>{env.ENV}</Badge>
          </header>
          <div className="flex justify-center py-16">
            <SignIn />
          </div>
        </div>
      </Show>
      <Show when="signed-in">
        <AdminGate />
      </Show>
    </>
  )
}

/** Playwright 전용 진입점 (VITE_E2E_TOKEN). 권한 확인은 같은 경로로 한다 */
export function E2EApp() {
  const path = useLocation().pathname
  if (path === '/factory' || path.startsWith('/factory/')) return <FactoryPortalPage />
  return <AdminGate />
}

function AdminGate() {
  const getToken = useGetToken()
  const [state, setState] = useState<'checking' | 'ok' | 'denied' | 'error'>('checking')
  const [message, setMessage] = useState('')
  const [access, setAccess] = useState(OWNER_FALLBACK)

  useEffect(() => {
    let alive = true
    adminApi
      .me(getToken)
      .then((me) => {
        if (!alive) return
        setAccess(me.access ?? OWNER_FALLBACK)
        setState('ok')
      })
      .catch((err: unknown) => {
        if (!alive) return
        const msg = toMessage(err)
        setMessage(msg)
        setState(msg.includes('Admin only') ? 'denied' : 'error')
      })
    return () => {
      alive = false
    }
  }, [getToken])

  if (state === 'ok')
    return (
      <AdminAccessContext.Provider value={access}>
        <Shell />
      </AdminAccessContext.Provider>
    )

  return (
    <div className="bg-kumo-base text-kumo-default min-h-screen p-8">
      {state === 'checking' && (
        <Surface className="flex items-center gap-3 rounded-lg p-6">
          <Loader />
          <Text variant="secondary">어드민 권한을 확인하는 중…</Text>
        </Surface>
      )}
      {state === 'denied' && (
        <Banner
          variant="error"
          title="어드민 계정이 아니에요"
          description="다른 어드민에게 [사용자 → 어드민] 에서 이 이메일을 추가해 달라고 하거나, 서버 설정 ADMIN_EMAILS 에 넣어야 해요."
        />
      )}
      {state === 'error' && (
        <Banner variant="error" title="API 에 연결하지 못했어요" description={message} />
      )}
    </div>
  )
}
